import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Center, Group, Loader, Paper, SimpleGrid, Stack, Text, ThemeIcon } from "@mantine/core";
import { PieChart } from "@mui/x-charts/PieChart";
import { IconAlertTriangle, IconExternalLink } from "@tabler/icons-react";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import { MyUserDataContext } from "../contexts";
import { Service, ServiceStatusCode } from "../types";

export const REVIEW_WINDOW_DAYS = 7;
// The KYC review tracking only covers KFH Trade update requests created on or
// after 1 August 2026 (local time); older requests are excluded from the charts.
export const KYC_REVIEW_START_DATE = new Date(2026, 7, 1);
const DAY_MS = 24 * 60 * 60 * 1000;
type PendingService = {
    id: string;
    name: string;
    civilId?: string;
    submittedAt: Date;
    dueAt: Date;
    daysRemaining: number;
    status?: string;
};

type KfhtradeKycReviewSummaryProps = {
    refreshToken?: number;
    onDaysRemainingSelect?: (days: number) => void;
    onKycStatusSelect?: (status: string) => void;
};

const startOfLocalDay = (value: Date) => {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
};

const KfhtradeKycReviewSummary = ({
    refreshToken = 0,
    onDaysRemainingSelect,
    onKycStatusSelect,
}: KfhtradeKycReviewSummaryProps) => {
    const userContext = useContext(MyUserDataContext);
    const [items, setItems] = useState<PendingService[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const searchClient = useMemo(() => new TypesenseInstantSearchAdapter({
        server: {
            apiKey: userContext.servicesSearchKey || "",
            nodes: [{
                host: import.meta.env.VITE_TYPESENSE_SEARCH_HOST!,
                port: 443,
                protocol: "https",
            }],
        },
        additionalSearchParameters: { query_by: "customerObj,tags" },
    }).searchClient, [userContext.servicesSearchKey]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(undefined);
            if (!userContext.servicesSearchKey) {
                setItems([]);
                setLoading(false);
                return;
            }
            try {
                const indexName = import.meta.env.VITE_TYPESENSE_SERVICES_COLLECTION!;
                const filters = [
                    "serviceType:=kfhtrade",
                    `tags.serviceStatus.code:=${ServiceStatusCode.waiting_for_kyc}`,
                    "isKfhtradeUpdate:=true",
                    `created:>=${Math.floor(KYC_REVIEW_START_DATE.getTime() / 1000)}`,
                ].join(" && ");
                const services: Service[] = [];
                let page = 0;
                let pageCount = 1;

                while (page < pageCount) {
                    const response = await searchClient.search([{
                        indexName,
                        params: { query: "*", filters, hitsPerPage: 250, page },
                    }]) as any;
                    const result = response.results?.[0];
                    // The InstantSearch adapter returns Algolia-style hits where each
                    // hit is already the indexed Typesense document (there is no
                    // nested `hit` property).
                    services.push(...((result?.hits || []) as Service[]));
                    pageCount = result?.nbPages || 0;
                    page += 1;
                }

                const today = startOfLocalDay(new Date());
                const pending = services.flatMap((service) => {
                    const createdSeconds = typeof service?.created === "number" ? service?.created : undefined;
                    const submittedAt = createdSeconds ? new Date(createdSeconds * 1000) : undefined;
                    const status = service?.tags?.kycStatus?.code;

                    if (!submittedAt) {
                        return [];
                    }

                    const submittedDay = startOfLocalDay(submittedAt);
                    const dueAt = new Date(submittedDay.getTime() + REVIEW_WINDOW_DAYS * DAY_MS);
                    const elapsedDays = Math.round((today.getTime() - submittedDay.getTime()) / DAY_MS);
                    return [{
                        id: service.id || "",
                        name: service.customerObj?.name_en || service.customerObj?.name_ar || service.id || "Unknown",
                        civilId: service.customerObj?.civilID,
                        submittedAt,
                        dueAt,
                        daysRemaining: Math.max(0, Math.min(REVIEW_WINDOW_DAYS, REVIEW_WINDOW_DAYS - elapsedDays)),
                        status,
                    }];
                }).sort((left, right) => left.dueAt.getTime() - right.dueAt.getTime());

                if (!cancelled) setItems(pending);
            } catch (loadError) {
                console.error("Failed to load pending KFH Trade KYC reviews", loadError);
                if (!cancelled) setError("The pending KFH Trade KYC reviews could not be loaded.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [refreshToken, searchClient, userContext.servicesSearchKey]);

    const chartData = useMemo(() => {
        const deadlineColors = ["#e03131", "#f76707", "#f59f00", "#fab005", "#94d82d", "#82c91e", "#51cf66", "#40c057"];

        return Array.from({ length: REVIEW_WINDOW_DAYS + 1 }, (_, day) => ({
            id: day,
            label: day === 0 ? "Overdue" : `${day} day${day === 1 ? "" : "s"} remaining`,
            value: items.filter((item) => item.daysRemaining === day).length,
            color: deadlineColors[day],
        })).filter((slice) => slice.value > 0);
    }, [items]);
    const kycStatusChartData = useMemo(() => {
        const counts = items.reduce<Record<string, number>>((result, item) => {
            if (item.status) result[item.status] = (result[item.status] || 0) + 1;
            return result;
        }, {});
        const colors = ["#228be6", "#f08c00", "#15aabf", "#7950f2", "#12b886", "#e64980", "#868e96"];

        return Object.entries(counts)
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([status, value], index) => ({
                id: status,
                label: status.replace(/^kyc_/, "").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
                value,
                color: colors[index % colors.length],
            }));
    }, [items]);
    const overdueCount = items.filter((item) => item.daysRemaining === 0).length;

    return (
        <Paper p="md" radius="md" withBorder shadow="xs">
            <Stack gap="sm">
                <Group justify="space-between">
                    <Group gap="xs">
                        <ThemeIcon color={overdueCount ? "red" : "orange"} variant="light">
                            <IconAlertTriangle size={18} />
                        </ThemeIcon>
                        <div>
                            <Text fw={700}>KFH Trade KYC reviews due</Text>

                        </div>
                    </Group>
                    <Badge color={overdueCount ? "red" : "orange"} size="lg">{items.length} pending</Badge>
                </Group>

                {error ? <Alert color="red">{error}</Alert> : loading ? (
                    <Center h={160}><Loader size="sm" /></Center>
                ) : (
                    <>
                        {overdueCount > 0 && <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                            {overdueCount} KYC review{overdueCount === 1 ? " is" : "s are"} overdue.
                        </Alert>}
                        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
                            <Paper withBorder radius="md" p="xs">
                                <Text size="sm" fw={700} ta="center">By review deadline</Text>
                                <PieChart
                                    series={[{
                                        data: chartData,
                                        innerRadius: 48,
                                        outerRadius: 80,
                                        paddingAngle: 2,
                                        cornerRadius: 4,
                                        valueFormatter: (item) => `${item.value} customer${item.value === 1 ? "" : "s"}`,
                                    }]}
                                    height={240}
                                    margin={{ left: 10, right: 220, top: 15, bottom: 15 }}
                                    slotProps={{ legend: { direction: "vertical", position: { vertical: "middle", horizontal: "right" } } }}
                                    onItemClick={onDaysRemainingSelect ? (_event, _identifier, item) => {
                                        if (typeof item.id === "number") onDaysRemainingSelect(item.id);
                                    } : undefined}
                                    skipAnimation
                                    sx={{ "& .MuiPieArc-root": { cursor: onDaysRemainingSelect ? "pointer" : "default" } }}
                                />
                            </Paper>
                            <Paper withBorder radius="md" p="xs">
                                <Text size="sm" fw={700} ta="center">By KYC status</Text>
                                <PieChart
                                    series={[{
                                        data: kycStatusChartData,
                                        innerRadius: 48,
                                        outerRadius: 80,
                                        paddingAngle: 2,
                                        cornerRadius: 4,
                                        valueFormatter: (item) => `${item.value} customer${item.value === 1 ? "" : "s"}`,
                                    }]}
                                    height={240}
                                    margin={{ left: 10, right: 220, top: 15, bottom: 15 }}
                                    slotProps={{ legend: { direction: "vertical", position: { vertical: "middle", horizontal: "right" } } }}
                                    onItemClick={onKycStatusSelect ? (_event, _identifier, item) => {
                                        if (typeof item.id === "string") onKycStatusSelect(item.id);
                                    } : undefined}
                                    skipAnimation
                                    sx={{ "& .MuiPieArc-root": { cursor: onKycStatusSelect ? "pointer" : "default" } }}
                                />
                            </Paper>
                        </SimpleGrid>

                    </>
                )}
            </Stack>
        </Paper>
    );
};

export default KfhtradeKycReviewSummary;
